import { useState, useEffect } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { customerApiService, transactionApiService, walletApiService, loanApiService } from 'services/api';
import { Employee, DashboardStats, Transaction } from 'types';

interface DashboardData {
  employees: Employee[];
  stats: DashboardStats;
  recentTransactions: Transaction[];
  loading: boolean;
  error: string | null;
}

export const useDashboard = () => {
  const { user, company } = useAuth();
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    employees: [],
    stats: {
      totalEmployees: 0,
      activeEmployees: 0,
      pendingApprovals: 0,
      totalTransactions: 0,
      totalWalletBalance: 0,
      totalCreditLimit: 0,
      totalCreditUsed: 0
    },
    recentTransactions: [],
    loading: true,
    error: null
  });

  const fetchDashboardData = async () => {
    if (!user || !company) {
      setDashboardData(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      setDashboardData(prev => ({ ...prev, loading: true, error: null }));

      // Fetch all data in parallel
      const [employeesResponse, transactionsResponse, walletStatsResponse, loanStatsResponse] = await Promise.allSettled([
        customerApiService.getEmployees(),
        transactionApiService.getTransactions({ page: 1, limit: 10 }),
        walletApiService.getWalletStatistics(),
        loanApiService.getLoanStatistics()
      ]);

      // Process employees data
      const employees = employeesResponse.status === 'fulfilled' && employeesResponse.value.success
        ? employeesResponse.value.data?.map((emp: any) => ({
            id: emp.id,
            name: `${emp.firstName} ${emp.lastName}`,
            employeeId: emp.employeeId,
            email: emp.email,
            firstName: emp.firstName,
            lastName: emp.lastName,
            phone: emp.phone,
            position: emp.position,
            department: emp.position || 'Unknown',
            grade: 'Associate', // Default grade
            status: (emp.isActive ? 'active' : 'inactive') as 'active' | 'inactive' | 'suspended' | 'pending',
            walletBalance: emp.walletBalance || 0,
            creditLimit: emp.creditLimit || 0,
            creditUsed: emp.creditUsed || 0,
            kycStatus: emp.kycStatus || 'PENDING',
            joinDate: emp.createdAt,
            createdAt: emp.createdAt,
            updatedAt: emp.updatedAt,
            isActive: emp.isActive,
            profileImage: emp.profileImage,
            dateOfBirth: emp.dateOfBirth,
            company: emp.company
          })) || []
        : [];

      // Process transactions data
      const recentTransactions = transactionsResponse.status === 'fulfilled' && transactionsResponse.value.success
        ? transactionsResponse.value.data?.transactions?.map((txn: any) => ({
            id: txn.id,
            customerId: txn.customerId,
            employeeName: 'Unknown Employee', // Will be resolved later
            employeeId: txn.customerId,
            amount: txn.amount,
            type: txn.type,
            category: txn.category,
            description: txn.description,
            reference: txn.reference,
            status: txn.status,
            date: txn.createdAt,
            createdAt: txn.createdAt,
            updatedAt: txn.updatedAt,
            recipientAccount: txn.recipientAccount,
            recipientBankCode: txn.recipientBankCode,
            recipientName: txn.recipientName,
            failureReason: txn.failureReason,
            meta: txn.meta
          })) || []
        : [];

      // Process wallet statistics
      const walletStats = walletStatsResponse.status === 'fulfilled' && walletStatsResponse.value.success
        ? walletStatsResponse.value.data || {}
        : {};

      // Process loan statistics
      const loanStats = loanStatsResponse.status === 'fulfilled' && loanStatsResponse.value.success
        ? loanStatsResponse.value.data || {}
        : {};

      // Calculate dashboard statistics
      const activeEmployees = employees.filter(emp => emp.status === 'active').length;
      const totalWalletBalance = employees.reduce((sum, emp) => sum + emp.walletBalance, 0);
      const totalCreditLimit = employees.reduce((sum, emp) => sum + emp.creditLimit, 0);
      const totalCreditUsed = employees.reduce((sum, emp) => sum + emp.creditUsed, 0);

      const stats: DashboardStats = {
        totalEmployees: employees.length,
        activeEmployees,
        pendingApprovals: 0, // Will be implemented when approval system is ready
        totalTransactions: recentTransactions.length,
        totalWalletBalance,
        totalCreditLimit,
        totalCreditUsed
      };

      setDashboardData({
        employees,
        stats,
        recentTransactions,
        loading: false,
        error: null
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setDashboardData(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch dashboard data'
      }));
    }
  };

  const refreshData = () => {
    fetchDashboardData();
  };

  useEffect(() => {
    fetchDashboardData();
  }, [user, company]);

  return {
    ...dashboardData,
    refreshData
  };
};
